import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import type { DeliveryPipelineReport, Percentiles, StatusBroken, StatusHealthState, StatusItem, StatusSection } from "@roomate/core";
import { fetchStatusMarkdown, useDeliveryPipeline, useProjects, useStatusReport } from "../api.ts";
import type { BoardFilter } from "./Project.tsx";
import { Ago, Button, cx, Empty, ErrorNote, Section } from "../ui.tsx";

// Every state carries a word as well as a color, so the banner never relies on color alone.
const HEALTH: Record<StatusHealthState, { word: string; tone: string; dot: string }> = {
  green: { word: "Passing", tone: "border-ok/50 bg-ok/10", dot: "bg-ok" },
  red: { word: "Failing", tone: "border-high/50 bg-high/10", dot: "bg-high" },
  stale: { word: "Stale", tone: "border-signal/50 bg-signal-soft", dot: "bg-signal" },
  unchecked: { word: "Unchecked", tone: "border-rule bg-paper-2", dot: "bg-ink-3" },
};

/** /status: what works, what is being built and what is broken, readable in one minute. Read-only for everyone. */
export function StatusPage() {
  const [params] = useSearchParams();
  const projects = useProjects();
  // No switcher exists, so default like the other pages (the first project); `?project=` picks another.
  const projectId = params.get("project") ?? projects.data?.[0]?.id ?? null;
  const project = projects.data?.find((p) => p.id === projectId) ?? null;
  const report = useStatusReport(projectId);
  const pipeline = useDeliveryPipeline(projectId);
  const [toast, setToast] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => () => void (timer.current !== null && window.clearTimeout(timer.current)), []);

  const say = (next: { tone: "ok" | "error"; text: string }) => {
    setToast(next);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 4000);
  };
  const copy = async () => {
    if (!projectId) return;
    try {
      await navigator.clipboard.writeText(await fetchStatusMarkdown(projectId));
      say({ tone: "ok", text: "Copied the status as Markdown" });
    } catch (e) {
      say({ tone: "error", text: `Could not copy: ${e instanceof Error ? e.message : "unknown error"}` });
    }
  };

  if (projects.isSuccess && !projectId) return <Empty>No project yet.</Empty>;
  // A failed live refetch keeps the last good report on screen; the error only replaces the page when there is none.
  if (!report.data) return report.error ? <ErrorNote error={report.error} /> : <div className="font-mono text-ink-3">loading…</div>;
  const r = report.data;
  const h = HEALTH[r.health.state];
  const board = `/projects/${r.project.id}`;
  const red = r.knownBroken.mainRed;
  const k = r.knownBroken;

  return (
    <div data-testid="status-page" className="mx-auto max-w-2xl space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">Status</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">{r.project.name}</h1>
        </div>
        <Button data-testid="status-copy-md" onClick={() => void copy()}>
          Copy as Markdown
        </Button>
      </header>

      <div
        data-testid="status-banner"
        data-state={r.health.state}
        title={project?.checkCommand ? `Check command: ${project.checkCommand}` : "No check command is configured"}
        className={cx("rounded-lg border px-4 py-3", h.tone)}
      >
        <div className="flex items-center gap-2.5">
          <span className={cx("h-2.5 w-2.5 shrink-0 rounded-full", h.dot)} aria-hidden />
          <span data-testid="status-banner-word" className="font-mono text-[13px] font-semibold uppercase tracking-[0.14em]">
            {h.word}
          </span>
          <span className="text-[14px]">{r.health.sentence}</span>
        </div>
        <p className="mt-1.5 font-mono text-[12px] text-ink-2">
          {r.health.sha ? <span data-testid="status-sha">main @ {r.health.sha.slice(0, 7)}</span> : "main unknown"}
          {r.health.checkedAt !== null && (
            <>
              {" · checked "}
              <Ago ts={r.health.checkedAt} />
            </>
          )}
        </p>
        {red && red.tailLines.length > 0 && (
          <details data-testid="status-tail" className="mt-2">
            <summary className="cursor-pointer text-[12px] text-ink-2">Output tail</summary>
            <pre className="mt-1.5 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-paper p-2 font-mono text-[11px] text-ink-2">{red.tailLines.join("\n")}</pre>
          </details>
        )}
      </div>

      {pipeline.data ? <DeliveryPipelinePanel pipeline={pipeline.data} /> : pipeline.error ? <ErrorNote error={pipeline.error} /> : <div className="font-mono text-[11px] text-ink-3">loading delivery metrics…</div>}

      <StatusList testid="status-works" title="Works today" section={r.worksToday} board={board} empty="Nothing shipped yet." moreLabel="shipped" filter="shipped" />
      <StatusList testid="status-progress" title="In progress" section={r.inProgress} board={board} empty="Nothing in progress." moreLabel="in progress" filter="in_progress" />
      <Section title="Known broken" aside={brokenCount(k)}>
        <div data-testid="status-broken">
          {k.empty ? (
            <Empty>Nothing known broken.</Empty>
          ) : (
            <div className="space-y-4">
              {red && (
                <p className="py-1.5 text-[14px]">
                  Main is failing its checks at <span className="font-mono">{red.sha.slice(0, 7)}</span>
                  {red.message ? ` — ${red.message}` : ""}
                </p>
              )}
              <Items label="Failed merges" section={k.mergeFailed} board={board} moreLabel="failed merges" filter="merge_failed" />
              <Items label="Open bugs" section={k.bugs} board={board} moreLabel="bugs" filter="bugs" />
              <Items label="Blocked" section={k.blocked} board={board} moreLabel="blocked" filter="blocked" />
            </div>
          )}
        </div>
      </Section>

      {toast && (
        <div
          role="status"
          data-testid="status-toast"
          className={cx(
            "fixed bottom-6 left-1/2 z-40 -translate-x-1/2 rounded-md border px-4 py-2 text-[13px] shadow-lg",
            toast.tone === "ok" ? "border-ok/50 bg-card text-ink" : "border-high/50 bg-card text-high",
          )}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

export function formatPipelineDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 60_000) return "<1m";
  const minutes = ms / 60_000;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  return `${(minutes / 60).toFixed(minutes >= 600 ? 0 : 1)}h`;
}

const pct = (value: number | null) => (value === null ? "—" : `${Math.round(value * 100)}%`);

function StageMetric({ label, value }: { label: string; value: Percentiles }) {
  return (
    <div className="rounded-md border border-rule/70 bg-paper-2 px-3 py-2">
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">{label}</p>
      <p className="mt-1 text-[14px] font-semibold">{formatPipelineDuration(value.p50Ms)} <span className="font-normal text-ink-3">P50</span></p>
      <p className="font-mono text-[11px] text-ink-3">{formatPipelineDuration(value.p95Ms)} P95 · {value.samples}</p>
    </div>
  );
}

/** Compact seven-day delivery panel; the recent rows keep the aggregate traceable to individual tasks. */
export function DeliveryPipelinePanel({ pipeline }: { pipeline: DeliveryPipelineReport }) {
  const s = pipeline.stages;
  return (
    <Section title="Delivery pipeline" aside={`last ${pipeline.windowDays} days`}>
      <div data-testid="delivery-pipeline" className="space-y-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <StageMetric label="Build" value={s.build} />
          <StageMetric label="Review" value={s.review} />
          <StageMetric label="Verify" value={s.verifying} />
          <StageMetric label="Queue wait" value={s.queueWait} />
          <StageMetric label="Merge check" value={s.mergeCheck} />
          <StageMetric label="Build → main" value={s.buildToMain} />
        </div>
        <p className="font-mono text-[11px] text-ink-3">
          Review rounds P50 {pipeline.reviewRounds.p50 ?? "—"} / P95 {pipeline.reviewRounds.p95 ?? "—"} · Merge check split P50/P95: typecheck {formatPipelineDuration(s.typecheck.p50Ms)}/{formatPipelineDuration(s.typecheck.p95Ms)} · vitest {formatPipelineDuration(s.vitest.p50Ms)}/{formatPipelineDuration(s.vitest.p95Ms)} · e2e {formatPipelineDuration(s.e2e.p50Ms)}/{formatPipelineDuration(s.e2e.p95Ms)}
        </p>
        <div className="grid grid-cols-3 gap-3 border-y border-rule/60 py-2 text-[12px]">
          <p><span className="block font-mono text-[10px] uppercase text-ink-3">Failures</span>{pipeline.mergeFailures.conflict} conflict · {pipeline.mergeFailures.checkFailed} check</p>
          <p><span className="block font-mono text-[10px] uppercase text-ink-3">Flaky</span>{pct(pipeline.flakyRate.rate)} · {pipeline.flakyRate.flaky}/{pipeline.flakyRate.rerunnableFailures}</p>
          <p><span className="block font-mono text-[10px] uppercase text-ink-3">Resolve overhead</span>{pct(pipeline.resolveOverhead.rate)} · {pipeline.resolveOverhead.resolveTasks}/{pipeline.resolveOverhead.realTasks}</p>
        </div>
        <div aria-label="Daily build to main P50" className="grid grid-cols-7 gap-1">
          {pipeline.overTime.map((day) => (
            <div key={day.day} className="min-w-0 text-center font-mono text-[10px] text-ink-3" title={`${day.day}: ${day.merged} merged, build to main P50 ${formatPipelineDuration(day.buildToMain.p50Ms)}`}>
              <span className="block truncate">{day.day.slice(5)}</span>
              <span className="block text-ink-2">{formatPipelineDuration(day.buildToMain.p50Ms)}</span>
            </div>
          ))}
        </div>
        {pipeline.tasks.length === 0 ? (
          <Empty>No tasks reached main in this window.</Empty>
        ) : (
          <ul className="divide-y divide-rule/60" aria-label="Recent delivery times">
            {pipeline.tasks.slice(0, 5).map((task) => (
              <li key={`${task.taskId}:${task.delivery}`} className="py-1.5 text-[12px]">
                <div className="flex items-baseline justify-between gap-3">
                  <Link to={`/tasks/${task.taskId}`} className="min-w-0 truncate hover:underline">#{task.number} {task.title}</Link>
                  <span className="shrink-0 font-mono text-ink-3">{formatPipelineDuration(task.buildToMainMs)} total · {task.reviewRounds} review {task.reviewRounds === 1 ? "round" : "rounds"}</span>
                </div>
                <p className="truncate font-mono text-[10px] text-ink-3">
                  build {formatPipelineDuration(task.buildMs)} · review {formatPipelineDuration(task.reviewMs)} · verify {formatPipelineDuration(task.verifyingMs)} · queue {formatPipelineDuration(task.queueWaitMs)} · check {formatPipelineDuration(task.mergeCheckMs)} (typecheck {formatPipelineDuration(task.checkDurations.typecheckMs)}/vitest {formatPipelineDuration(task.checkDurations.vitestMs)}/e2e {formatPipelineDuration(task.checkDurations.e2eMs)}) · failures {task.mergeFailures.conflict} conflict/{task.mergeFailures.checkFailed} check/{task.mergeFailures.flaky} flaky · {task.resolveTasks} resolve
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

function brokenCount(k: StatusBroken): string | null {
  const n = (k.mainRed ? 1 : 0) + [k.mergeFailed, k.bugs, k.blocked].reduce((sum, s) => sum + s.items.length + s.more, 0);
  return n ? String(n) : null;
}

function StatusList({ testid, title, section, board, empty, moreLabel, filter }: { testid: string; title: string; section: StatusSection; board: string; empty: string; moreLabel: string; filter: BoardFilter }) {
  const count = section.items.length + section.more;
  return (
    <Section title={title} aside={count || null}>
      <div data-testid={testid}>{count === 0 ? <Empty>{empty}</Empty> : <Items section={section} board={board} moreLabel={moreLabel} filter={filter} />}</div>
    </Section>
  );
}

function Items({ label, section, board, moreLabel, filter }: { label?: string; section: StatusSection; board: string; moreLabel: string; filter: BoardFilter }) {
  if (section.items.length === 0 && section.more === 0) return null;
  return (
    <div>
      {label && <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">{label}</p>}
      <ul className="divide-y divide-rule/60">
        {section.items.map((item) => (
          <ItemRow key={`${item.href}:${item.title}`} item={item} />
        ))}
      </ul>
      {section.more > 0 && (
        <Link data-testid={`status-more-${filter}`} to={`${board}?show=${filter}`}className="mt-1 inline-block text-[12px] text-ink-2 underline hover:text-ink">
          +{section.more} more {moreLabel} on the board
        </Link>
      )}
    </div>
  );
}

function ItemRow({ item }: { item: StatusItem }) {
  return (
    <li className="flex items-baseline justify-between gap-3 py-1.5 text-[14px]">
      <Link to={item.href} className="min-w-0 truncate hover:underline">
        {item.title}
      </Link>
      {item.meta && <span className="shrink-0 text-[12px] text-ink-3">{item.meta}</span>}
    </li>
  );
}
